import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Search, X } from "lucide-react";
import { toast } from "react-toastify";
import api from "~/services/api";
import { USDollar, formatDate, toNumber } from "./utils";

const DEBOUNCE_MS = 450;
const LIMIT = 10;

export default function DelinquencyAgingDetailModal({ isOpen, onClose, drill }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedTerm((searchTerm || "").trim()),
      DEBOUNCE_MS
    );
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (isOpen && drill?.reference_date) {
      setCurrentPage(1);
      setSearchTerm("");
      setDebouncedTerm("");
      loadDetailData();
    }
  }, [isOpen, drill]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedTerm]);

  async function loadDetailData() {
    setLoading(true);
    try {
      const { data: response } = await api.get(
        "/reports/receivables/delinquency-aging/detail",
        {
          params: {
            reference_date: drill.reference_date,
            aging_key: drill.aging_key || undefined,
            student_id: drill.student_id || undefined,
            issuer_id: drill.issuer_id || undefined,
            orderBy: "due_date",
            orderASC: "ASC",
          },
        }
      );
      setData(response);
    } catch (err) {
      toast(err?.response?.data?.error || "Error loading detail data", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  const normalized = (value) =>
    (value || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    const term = normalized(debouncedTerm);
    if (!term) return data.rows;
    return data.rows.filter(
      (row) =>
        normalized(row.invoice_number).includes(term) ||
        normalized(row.student_name).includes(term) ||
        normalized(row.registration_number).includes(term) ||
        normalized(row.issuer?.name).includes(term) ||
        normalized(row.filial?.name).includes(term)
    );
  }, [data?.rows, debouncedTerm]);

  const totalRowsUI = filteredRows.length;
  const totalPages = totalRowsUI ? Math.ceil(totalRowsUI / LIMIT) : 1;
  const pageSlice = useMemo(() => {
    const start = (currentPage - 1) * LIMIT;
    return filteredRows.slice(start, start + LIMIT);
  }, [filteredRows, currentPage]);
  const startRecord = totalRowsUI ? (currentPage - 1) * LIMIT + 1 : 0;
  const endRecord = totalRowsUI
    ? Math.min(currentPage * LIMIT, totalRowsUI)
    : 0;

  if (!isOpen) return null;

  const totals = data?.totals || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div
        className={`bg-white rounded-lg shadow-xl ${
          isMobile ? "w-full h-full" : "w-full max-w-7xl max-h-[90vh]"
        } flex flex-col`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>
              Delinquency Aging Details — {drill?.title}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Reference date {drill?.reference_date} ·{" "}
              {USDollar.format(toNumber(totals.net_balance))} open balance
              {data?.totalRows ? ` · ${data.totalRows} invoices` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 border-b bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 text-sm text-gray-700">
            {totalRowsUI > 0 && (
              <span>
                Showing <span className="font-semibold">{startRecord}</span> to{" "}
                <span className="font-semibold">{endRecord}</span> of{" "}
                <span className="font-semibold">{totalRowsUI}</span> rows
              </span>
            )}
          </div>
          <div className="relative w-full sm:w-80">
            <input
              type="text"
              placeholder="Search by invoice, student, issuer, branch..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-hidden focus:ring-2 focus:ring-green-500"
            />
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="animate-spin" size={36} />
            </div>
          ) : pageSlice.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 bg-white shadow-xs">
                  <tr className="bg-gray-100 border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Invoice
                    </th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Student
                    </th>
                    {!isMobile && (
                      <th className="text-left p-3 text-sm font-semibold text-gray-700">
                        Branch
                      </th>
                    )}
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Due Date
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Days
                    </th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Aging
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Amount
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Discount
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Disc. Pending
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Paid
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Fee
                    </th>
                    <th className="text-right p-3 text-sm font-semibold text-gray-700">
                      Balance
                    </th>
                    {!isMobile && (
                      <th className="text-center p-3 text-sm font-semibold text-gray-700">
                        Status
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {pageSlice.map((row, index) => (
                    <tr
                      key={row.id ?? `${row.invoice_number}-${index}`}
                      className={`border-b hover:bg-green-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      <td className="p-3 text-sm font-medium text-gray-900">
                        {row.invoice_number}
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {row.student_name || row.issuer?.name || "-"}
                        {row.registration_number && (
                          <span className="text-gray-400">
                            {" "}
                            · {row.registration_number}
                          </span>
                        )}
                      </td>
                      {!isMobile && (
                        <td className="p-3 text-sm text-gray-700">
                          {row.filial?.name || "-"}
                        </td>
                      )}
                      <td className="p-3 text-sm text-gray-700">
                        {formatDate(row.due_date)}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-700">
                        {toNumber(row.days_overdue)}
                      </td>
                      <td className="p-3 text-sm text-gray-700">
                        {row.aging_label || "-"}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-900">
                        {USDollar.format(toNumber(row.amount))}
                      </td>
                      <td className="p-3 text-sm text-right text-red-600">
                        {toNumber(row.discount)
                          ? `(${USDollar.format(toNumber(row.discount))})`
                          : "-"}
                      </td>
                      <td className="p-3 text-sm text-right text-red-600">
                        {toNumber(row.discount_pending)
                          ? `(${USDollar.format(toNumber(row.discount_pending))})`
                          : "-"}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-900">
                        {toNumber(row.paid)
                          ? USDollar.format(toNumber(row.paid))
                          : "-"}
                      </td>
                      <td className="p-3 text-sm text-right text-gray-900">
                        {toNumber(row.fee_balance)
                          ? USDollar.format(toNumber(row.fee_balance))
                          : "-"}
                      </td>
                      <td className="p-3 text-sm text-right font-semibold text-gray-900">
                        {USDollar.format(toNumber(row.net_balance))}
                      </td>
                      {!isMobile && (
                        <td className="p-3 text-sm text-center">
                          <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                            {row.status}
                          </span>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold text-sm">
                    <td className="p-3" colSpan={isMobile ? 5 : 6}>
                      Totals
                    </td>
                    <td className="p-3 text-right">
                      {USDollar.format(toNumber(totals.amount))}
                    </td>
                    <td className="p-3 text-right text-red-600">
                      {toNumber(totals.discount)
                        ? `(${USDollar.format(toNumber(totals.discount))})`
                        : "-"}
                    </td>
                    <td className="p-3 text-right text-red-600">
                      {toNumber(totals.discount_pending)
                        ? `(${USDollar.format(toNumber(totals.discount_pending))})`
                        : "-"}
                    </td>
                    <td className="p-3 text-right">
                      {USDollar.format(toNumber(totals.paid))}
                    </td>
                    <td className="p-3 text-right">
                      {USDollar.format(toNumber(totals.fee_balance))}
                    </td>
                    <td className="p-3 text-right">
                      {USDollar.format(toNumber(totals.net_balance))}
                    </td>
                    {!isMobile && <td />}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              {debouncedTerm
                ? `No results found for "${debouncedTerm}"`
                : "No overdue receivables found for this selection"}
            </div>
          )}
        </div>

        {totalRowsUI > 0 && (
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-sm px-3 font-medium text-gray-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors bg-white"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
